import React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
    MdAssignmentTurnedIn,
    MdAttachFile,
    MdBadge,
    MdBusiness,
    MdCalendarMonth,
    MdDescription,
    MdDownload,
    MdEmail,
    MdEdit,
    MdFactCheck,
    MdGroups,
    MdLocationOn,
    MdMedicalServices,
    MdPerson,
    MdPhone,
    MdSchool,
    MdVisibility,
    MdWork,
    MdHealing,
} from "react-icons/md";
import { toast } from "react-toastify";
import FileActions from '../../Components/Common/FileActions';
import {
    getAllProviderControlledSubstanceLicenses,
    getAllProviderDEALicenses,
    getAllProviderEducations,
    getAllProviderEmployers,
    getAllProviderExams,
    getAllProviderFacilities,
    getAllProviderMilitary,
    getAllProviderPeerReferences,
    getAllProviderStateLicenses,
    getAllProviderWorkHistory,
    getProviderById,
    getAllProviderBoardCertificates,
    getAllProviderProfessionalCertificates,
    getAllProviderAdditionalCertificates,
    getAllProviderMalpracticeInfo,
    getAllProviderHealthInfo,
} from "../../Services/ProviderService";
import { APPRoutes } from "../../Utils/Route";
import { formatUtcToLocalDate } from "../../Utils/DateTime";
import { baseurl, endpoints } from "../../Utils/Constants";
import { useAuth } from "../../Context/AuthContext";
import { PermissionCodes, permissionHas, resolveUserPermissionCodes } from "../../Utils/Permissions";
import { previewProviderFile, downloadProviderFile } from "../../Services/ProviderService";
import ViewEmptyState from "../../Components/Common/ViewEmptyState";
import BackButton from '../../Components/Common/BackButton';



const STATUS_STYLE = {
    Active: { backgroundColor: "var(--hc-status-success-bg)", color: "var(--hc-status-success-text-strong)" },
    Inactive: { backgroundColor: "var(--hc-status-neutral-bg)", color: "var(--hc-table-footer-text)" },
    Pending: { backgroundColor: "var(--hc-status-pending-alt-bg)", color: "var(--hc-blue-700)" },
    Suspended: { backgroundColor: "var(--hc-status-pending-bg)", color: "var(--hc-status-pending-text)" },
    Unknown: { backgroundColor: "var(--hc-status-unknown-bg)", color: "var(--hc-status-unknown-text)" },
};

const formatValue = (value) => {
    if (value === null || value === undefined || value === "") {
        return "-";
    }

    return value;
};

const formatDate = (value) => formatUtcToLocalDate(value);

const formatBoolean = (value) => {
    if (value === true) {
        return "Yes";
    }

    if (value === false) {
        return "No";
    }

    return "-";
};

const formatAddress = (address) => {
    if (!address) {
        return "-";
    }

    const pieces = [
        address.street || address.Street,
        address.street2 || address.Street2,
        address.city || address.City,
        address.state || address.State,
        address.country || address.Country,
        address.zipcode || address.Zipcode,
    ].filter(Boolean);

    return pieces.length ? pieces.join(", ") : "-";
};

const buildFullName = (provider) => {
    const parts = [
        provider?.prefix_name || provider?.prefixName,
        provider?.first_name || provider?.firstName,
        provider?.middle_name || provider?.middleName,
        provider?.last_name || provider?.lastName,
        provider?.suffix_name || provider?.suffixName,
    ].filter(Boolean);

    return parts.length ? parts.join(" ") : "Provider";
};

const getDataArray = (response) => {
    if (!response) {
        return [];
    }

    if (Array.isArray(response)) {
        return response;
    }

    if (Array.isArray(response?.data)) {
        return response.data;
    }

    return [];
};

const ViewSkeleton = () => (
    <div className="container-fluid py-4 hc-page-bg min-vh-100">
        <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between mb-3">
            <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 40, width: 140, borderRadius: 999 }} />
            <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 40, width: 120, borderRadius: 12 }} />
        </div>

        <div className="p-4 p-lg-5 mb-4 overflow-hidden hc-hero-banner position-relative hc-hero-banner-primary">
            <div aria-hidden="true" className="hc-hero-circle-tr position-absolute rounded-circle pe-none" />
            <div aria-hidden="true" className="hc-hero-circle-bl position-absolute rounded-circle pe-none" />
            <div className="position-relative">
                <div className="d-flex flex-wrap gap-2 mb-3">
                    <div className="skeleton-hero position-relative overflow-hidden rounded-2" style={{ height: 28, width: 100, borderRadius: 999 }} />
                    <div className="skeleton-hero position-relative overflow-hidden rounded-2" style={{ height: 28, width: 100, borderRadius: 999 }} />
                </div>
                <div className="skeleton-hero mb-3" style={{ height: 36, width: "60%", borderRadius: 12 }} />
                <div className="skeleton-hero mb-2 rounded-3" style={{ height: 18, width: "40%" }} />
                <div className="skeleton-hero mb-2 rounded-3" style={{ height: 18, width: "30%" }} />
                <div className="row g-3 mt-4">
                    <div className="col-sm-4">
                        <div className="skeleton-hero position-relative overflow-hidden rounded-2" style={{ height: 80, borderRadius: 20 }} />
                    </div>
                    <div className="col-sm-4">
                        <div className="skeleton-hero position-relative overflow-hidden rounded-2" style={{ height: 80, borderRadius: 20 }} />
                    </div>
                    <div className="col-sm-4">
                        <div className="skeleton-hero position-relative overflow-hidden rounded-2" style={{ height: 80, borderRadius: 20 }} />
                    </div>
                </div>
            </div>
        </div>

        <div className="row g-4 mb-4">
            <div className="col-12 col-lg-8">
                <div className="row g-3">
                    {Array.from({ length: 4 }).map((_, idx) => (
                        <div className="col-12 col-md-6" key={idx}>
                            <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 120, borderRadius: 20 }} />
                        </div>
                    ))}
                </div>
            </div>
            <div className="col-12 col-lg-4">
                <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 320, borderRadius: 24 }} />
            </div>
        </div>

        <div className="row g-4 mb-4">
            <div className="col-12 col-lg-6">
                <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 240, borderRadius: 24 }} />
            </div>
            <div className="col-12 col-lg-6">
                <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 240, borderRadius: 24 }} />
            </div>
        </div>

        <div className="row g-4">
            <div className="col-12">
                <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 180, borderRadius: 24 }} />
            </div>
            <div className="col-12 col-lg-6">
                <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 180, borderRadius: 24 }} />
            </div>
            <div className="col-12 col-lg-6">
                <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 180, borderRadius: 24 }} />
            </div>
        </div>
    </div>
);

const StatCard = ({ label, value, icon: Icon, iconBg, valueClass }) => (
    <div className="position-relative overflow-hidden p-4 rounded-4 hc-surface hc-card-primary h-100 shadow-sm">
        <div className="position-absolute start-0 top-0 w-100" />
        <div className="d-flex align-items-center gap-3">
            <div
                className="d-inline-flex align-items-center justify-content-center rounded-3"
                style={{ width: 44, height: 44, backgroundColor: iconBg }}
            >
                <Icon size={20} />
            </div>
            <div className="d-flex flex-column fw-bold lh-sm">
                <span className="text-dark" style={{ fontSize: "0.85rem" }}>
                    {label}
                </span>
                <span className={`fw-bold ${valueClass}`} style={{ fontSize: "1.1rem" }}>
                    {value}
                </span>
            </div>
        </div>
    </div>
);

const DetailRow = ({ icon: Icon, label, value }) => (
    <div className="d-flex align-items-start gap-3 py-3 border-bottom border-light-subtle">
        <span
            className="d-inline-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
            style={{ width: 36, height: 36, background: "linear-gradient(135deg, rgba(var(--hc-accent-blue-rgb), 0.08) 0%, rgba(var(--hc-accent-blue-rgb), 0.12) 100%)", color: "var(--hc-accent-blue)" }}
        >
            <Icon size={16} />
        </span>
        <div className="flex-grow-1 min-w-0">
            <div className="small text-muted mb-1">{label}</div>
            <div className="fw-medium text-dark text-break" style={{ fontSize: "0.9rem" }}>
                {value || "-"}
            </div>
        </div>
    </div>
);

const SectionCard = ({ title, icon: Icon, children }) => (
    <div className="p-4 p-lg-5 hc-surface hc-card-primary h-100 position-relative overflow-hidden shadow-sm">
        <div className="position-absolute start-0 top-0 w-100" />
        <div className="d-flex align-items-center gap-2 mb-4">
            <span
                className="d-inline-flex align-items-center justify-content-center rounded-circle hc-text-primary"
                style={{ width: 38, height: 38, backgroundColor: "rgba(var(--hc-accent-blue-rgb), 0.08)", }}
            >
                <Icon size={18} />
            </span>
            <h5 className="mb-0 fw-bold hc-text-primary">{title}</h5>
        </div>
        {children}
    </div>
);

const DocumentBadge = ({ item }) => {
    if (!item?.file_id && !item?.file_name) {
        return null;
    }

    return (
        <span className="d-inline-flex align-items-center gap-2 rounded-pill border px-3 py-1 mt-2" style={{ color: "var(--hc-accent-blue)", borderColor: "rgba(var(--hc-accent-blue-rgb), 0.2)", backgroundColor: "rgba(var(--hc-accent-blue-rgb), 0.06)" }}>
            <MdAttachFile size={14} />
            {item.file_name || "Document attached"}
        </span>
    );
};

const DetailInfoCard = ({ label, value, actions }) => (
    <div className="col-12 col-md-6 col-xl-4">
        <div className="h-100 rounded-4 border border-light-subtle bg-light px-3 py-3">
            <div className="small mb-1 w-100 hc-text-primary fw-semibold text-break" style={{ wordBreak: "break-word", overflowWrap: "break-word" }}>{label}</div>
            {actions ? actions : <div className="fw-medium text-dark text-break" style={{ wordBreak: "break-word", overflowWrap: "break-word" }}>{value || "-"}</div>}
        </div>
    </div>
);

export default function ProvidersView() {
    const navigate = useNavigate();
    const { id } = useParams();
    const location = useLocation();
    const { token, permissions, decodedToken } = useAuth();
    const userPermissionCodes = React.useMemo(
        () => resolveUserPermissionCodes({ permissions, decodedToken }),
        [permissions, decodedToken]
    );
    const canAccessPage = permissionHas(userPermissionCodes, PermissionCodes.PROVIDERS_VIEW, decodedToken);
    const canEditProvider = permissionHas(userPermissionCodes, PermissionCodes.PROVIDERS_EDIT, decodedToken);
    const canViewList = permissionHas(userPermissionCodes, PermissionCodes.PROVIDERS_LIST, decodedToken);

    const providerFromState = React.useMemo(() => location.state?.provider || null, [location.state?.provider]);
    const [provider, setProvider] = React.useState(providerFromState);
    const [exams, setExams] = React.useState([]);
    const [educations, setEducations] = React.useState([]);
    const [employers, setEmployers] = React.useState([]);
    const [facilities, setFacilities] = React.useState([]);
    const [workHistory, setWorkHistory] = React.useState([]);
    const [military, setMilitary] = React.useState([]);
    const [peerReferences, setPeerReferences] = React.useState([]);
    const [healthInfos, setHealthInfos] = React.useState([]);
    const [malpractices, setMalpractices] = React.useState([]);
    const [boardCertificates, setBoardCertificates] = React.useState([]);
    const [professionalCertificates, setProfessionalCertificates] = React.useState([]);
    const [additionalCertificates, setAdditionalCertificates] = React.useState([]);
    const [stateLicenses, setStateLicenses] = React.useState([]);
    const [deaLicenses, setDealicenses] = React.useState([]);
    const [controlledLicenses, setControlledLicenses] = React.useState([]);

    const [downloadingId, setDownloadingId] = React.useState(null);
    const resolvedId = React.useMemo(() => id || providerFromState?.Id || decodedToken?.provider_id || null, [id, providerFromState, decodedToken]);
    const [loading, setLoading] = React.useState(!providerFromState);
    const [detailsLoading, setDetailsLoading] = React.useState(Boolean(token && resolvedId));
    const [sectionLoading, setSectionLoading] = React.useState({
        education: true,
        exams: true,
        employers: true,
        facilities: true,
        workHistory: true,
        military: true,
        peerReferences: true,
        malpractices: true,
        healthInfos: true,
        boardCertificates: true,
        professionalCertificates: true,
        additionalCertificates: true,
        stateLicenses: true,
        deaLicenses: true,
        controlledLicenses: true,
    });
    const [educationPage, setEducationPage] = React.useState(0);
    const [examPage, setExamPage] = React.useState(0);
    const [employerPage, setEmployerPage] = React.useState(0);
    const [facilityPage, setFacilityPage] = React.useState(0);
    const [workHistoryPage, setWorkHistoryPage] = React.useState(0);
    const [militaryPage, setMilitaryPage] = React.useState(0);
    const [peerReferencePage, setPeerReferencePage] = React.useState(0);
    const [healthInfoPage, setHealthInfoPage] = React.useState(0);
    const [malpracticePage, setMalpracticePage] = React.useState(0);
    const [boardCertificatePage, setBoardCertificatePage] = React.useState(0);
    const [professionalCertificatePage, setProfessionalCertificatePage] = React.useState(0);
    const [additionalCertificatePage, setAdditionalCertificatePage] = React.useState(0);
    const [stateLicensePage, setStateLicensePage] = React.useState(0);
    const [deaLicensePage, setDeaLicensePage] = React.useState(0);
    const [controlledLicensePage, setControlledLicensePage] = React.useState(0);
    const pageSize = 1;



    const renderFileActionButtons = (item) => {
        return (
            <FileActions
                fileId={item?.file_id}
                fileName={item?.file_name}
                className="w-100"
                onDownload={async (token, fid) => {
                    return await downloadProviderFile(token, fid);
                }}
                onPreview={async (token, fid) => {
                    return await previewProviderFile(token, fid);
                }}
            />
        );
    };



    const pagedEducations = React.useMemo(() => {
        const start = educationPage * pageSize;
        return educations.slice(start, start + pageSize);
    }, [educations, educationPage]);

    const pagedExams = React.useMemo(() => {
        const start = examPage * pageSize;
        return exams.slice(start, start + pageSize);
    }, [exams, examPage]);

    const pagedEmployers = React.useMemo(() => {
        const start = employerPage * pageSize;
        return employers.slice(start, start + pageSize);
    }, [employers, employerPage]);

    const pagedFacilities = React.useMemo(() => {
        const start = facilityPage * pageSize;
        return facilities.slice(start, start + pageSize);
    }, [facilities, facilityPage]);

    const pagedWorkHistory = React.useMemo(() => {
        const start = workHistoryPage * pageSize;
        return workHistory.slice(start, start + pageSize);
    }, [workHistory, workHistoryPage]);

    const pagedMilitary = React.useMemo(() => {
        const start = militaryPage * pageSize;
        return military.slice(start, start + pageSize);
    }, [military, militaryPage]);

    const pagedPeerReferences = React.useMemo(() => {
        const start = peerReferencePage * pageSize;
        return peerReferences.slice(start, start + pageSize);
    }, [peerReferences, peerReferencePage]);

    const pagedBoardCertificates = React.useMemo(() => {
        const start = boardCertificatePage * pageSize;
        return boardCertificates.slice(start, start + pageSize);
    }, [boardCertificates, boardCertificatePage]);

    const pagedProfessionalCertificates = React.useMemo(() => {
        const start = professionalCertificatePage * pageSize;
        return professionalCertificates.slice(start, start + pageSize);
    }, [professionalCertificates, professionalCertificatePage]);

    const pagedAdditionalCertificates = React.useMemo(() => {
        const start = additionalCertificatePage * pageSize;
        return additionalCertificates.slice(start, start + pageSize);
    }, [additionalCertificates, additionalCertificatePage]);

    const pagedMalpractices = React.useMemo(() => {
        const start = malpracticePage * pageSize;
        return malpractices.slice(start, start + pageSize);
    }, [malpractices, malpracticePage]);

    const pagedHealthInfos = React.useMemo(() => {
        const start = healthInfoPage * pageSize;
        return healthInfos.slice(start, start + pageSize);
    }, [healthInfos, healthInfoPage]);

    const pagedStateLicenses = React.useMemo(() => {
        const start = stateLicensePage * pageSize;
        return stateLicenses.slice(start, start + pageSize);
    }, [stateLicenses, stateLicensePage]);

    const pagedDEALicenses = React.useMemo(() => {
        const start = deaLicensePage * pageSize;
        return deaLicenses.slice(start, start + pageSize);
    }, [deaLicenses, deaLicensePage]);

    const pagedControlledLicenses = React.useMemo(() => {
        const start = controlledLicensePage * pageSize;
        return controlledLicenses.slice(start, start + pageSize);
    }, [controlledLicenses, controlledLicensePage]);

    React.useEffect(() => {
        if (!token || !resolvedId) {
            return;
        }

        let isMounted = true;

        const loadSection = async (requestFn, setData, sectionKey) => {
            try {
                const response = await requestFn();
                if (!isMounted) {
                    return;
                }

                setData(response?.is_success ? getDataArray(response) : []);
            } catch (error) {
                console.error(error);
                if (isMounted) {
                    setData([]);
                }
            } finally {
                if (isMounted) {
                    setSectionLoading((prevState) => ({ ...prevState, [sectionKey]: false }));
                }
            }
        };

        const loadDetails = async () => {
            try {
                setLoading(true);
                setDetailsLoading(true);
                setSectionLoading({
                    education: true,
                    exams: true,
                    employers: true,
                    facilities: true,
                    workHistory: true,
                    military: true,
                    peerReferences: true,
                    malpractices: true,
                    healthInfos: true,
                    boardCertificates: true,
                    professionalCertificates: true,
                    additionalCertificates: true,
                    stateLicenses: true,
                    deaLicenses: true,
                    controlledLicenses: true,
                });

                // No identity to resolve to (e.g. a view-only user with no provider_id claim) --
                // there is no "first record" fallback anymore; show the empty state instead.
                let targetId = resolvedId && resolvedId !== "first" && resolvedId !== ":id" ? resolvedId : null;

                if (providerFromState && !(!resolvedId || resolvedId === "first" || resolvedId === ":id")) {
                    setProvider(providerFromState);
                    setLoading(false);
                } else {
                    if (!targetId) {
                        setProvider(null);
                        setLoading(false);
                        setDetailsLoading(false);
                        return;
                    }
                    const providerResponse = await getProviderById(token, targetId);
                    if (isMounted && providerResponse?.is_success) {
                        setProvider(providerResponse.data || null);
                        setLoading(false);
                    } else if (isMounted) {
                        setProvider(null);
                        toast.error(providerResponse?.message || "Failed to load provider details");
                        setLoading(false);
                        setDetailsLoading(false);
                        return;
                    }
                }

                await Promise.all([
                    loadSection(() => getAllProviderExams(token, targetId), setExams, "exams"),
                    loadSection(() => getAllProviderEducations(token, targetId), setEducations, "education"),
                    loadSection(() => getAllProviderEmployers(token, targetId), setEmployers, "employers"),
                    loadSection(() => getAllProviderFacilities(token, targetId), setFacilities, "facilities"),
                    loadSection(() => getAllProviderWorkHistory(token, targetId), setWorkHistory, "workHistory"),
                    loadSection(() => getAllProviderMilitary(token, targetId), setMilitary, "military"),
                    loadSection(() => getAllProviderPeerReferences(token, targetId), setPeerReferences, "peerReferences"),
                    loadSection(() => getAllProviderMalpracticeInfo(token, targetId), setMalpractices, "malpractices"),
                    loadSection(() => getAllProviderHealthInfo(token, targetId), setHealthInfos, "healthInfos"),
                    loadSection(() => getAllProviderBoardCertificates(token, targetId), setBoardCertificates, "boardCertificates"),
                    loadSection(() => getAllProviderProfessionalCertificates(token, targetId), setProfessionalCertificates, "professionalCertificates"),
                    loadSection(() => getAllProviderAdditionalCertificates(token, targetId), setAdditionalCertificates, "additionalCertificates"),
                    loadSection(() => getAllProviderStateLicenses(token, targetId), setStateLicenses, "stateLicenses"),
                    loadSection(() => getAllProviderDEALicenses(token, targetId), setDealicenses, "deaLicenses"),
                    loadSection(() => getAllProviderControlledSubstanceLicenses(token, targetId), setControlledLicenses, "controlledLicenses"),
                ]);
            } catch (error) {
                console.error(error);
                if (isMounted) {
                    toast.error("An error occurred while loading provider details");
                }
            } finally {
                if (isMounted) {
                    setLoading(false);
                    setDetailsLoading(false);
                }
            }
        };

        loadDetails();

        return () => {
            isMounted = false;
        };
    }, [token, resolvedId, location.state?.provider]);

    if (loading) {
        return <ViewSkeleton />;
    }

    if (!provider) {
        return (
            <ViewEmptyState
                title="No Providers Available"
                description="There are no provider records available to display."
                icon={MdHealing}
                canViewList={canViewList}
                backRoute={APPRoutes.PROVIDERS}
                backLabel="Back to Providers"
            />
        );
    }

    if (!canAccessPage) {
        return (
            <div className="container-fluid py-4">
                <div className="alert alert-danger">You do not have permission to view providers.</div>
            </div>
        );
    }

    const statusName = provider?.status_name || provider?.StatusName || "Unknown";
    const statusMeta = STATUS_STYLE[statusName] || STATUS_STYLE.Unknown;
    const fullName = buildFullName(provider);
    const primaryAddress = provider?.address || provider?.Address || null;
    const birthAddress = provider?.birth_address || provider?.birthAddress || null;
    const primaryLocationAddress = provider?.primary_location_address || provider?.primaryLocationAddress || null;
    const secondaryLocationAddress = provider?.secondary_location_address || provider?.secondaryLocationAddress || null;

    return (
        <div className="container-fluid py-4 hc-page-bg min-vh-100">
            <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between mb-3">
                {canViewList && (
                    <BackButton onClick={() => navigate(APPRoutes.PROVIDERS)} />
                )}

                {canEditProvider ? (
                    <button
                        type="button"
                        className="btn hc-btn-next border-0 d-inline-flex align-items-center gap-2 px-3 py-2 rounded-pill"
                        onClick={() => navigate(APPRoutes.PROVIDERS_EDIT.replace(':id', provider?.Id ?? provider?.id ?? id), { state: { provider } })}
                    >
                        <MdEdit size={18} />
                        <span>Edit Provider</span>
                    </button>
                ) : null}
            </div>



            <div className="p-4 p-lg-5 mb-4 overflow-hidden hc-hero-banner position-relative hc-hero-banner-primary">
                <div aria-hidden="true" className="hc-hero-circle-tr position-absolute rounded-circle pe-none" />
                <div aria-hidden="true" className="hc-hero-circle-bl position-absolute rounded-circle pe-none" />

                <div className="row g-4 align-items-stretch position-relative">
                    <div className="col-lg-12 col-xl-8 d-flex flex-column h-100">
                        <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
                            <span
                                className="d-inline-flex align-items-center gap-2 px-3 py-2 rounded-pill fw-semibold"
                                style={{ fontSize: "0.82rem", ...statusMeta }}
                            >
                                <MdAssignmentTurnedIn size={16} />
                                {statusName}
                            </span>
                            <span className="px-3 py-2 rounded-pill fw-semibold text-white" style={{ fontSize: "0.82rem", backgroundColor: "rgba(var(--hc-white-rgb), 0.16)" }}>
                                {provider?.organization_name || "-"}
                            </span>
                        </div>

                        <h2 className="fw-bold text-white mb-2" style={{ fontSize: "clamp(2rem, 2.6vw, 3rem)" }}>{fullName}</h2>
                        <p className="text-white-50 mb-3" style={{ maxWidth: 700 }}>
                            {provider?.provider_title_name || "Provider profile"}
                        </p>

                        <div className="row g-3 mb-3">
                            <div className="col-md-6">
                                <div className="p-3 rounded-4 border border-white/20 text-white h-100" style={{ backgroundColor: "rgba(var(--hc-white-rgb), 0.10)" }}>
                                    <div className="small text-white-50 mb-1">Primary organization</div>
                                    <div className="fw-semibold">{provider?.organization_name || "-"}</div>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="p-3 rounded-4 border border-white/20 text-white h-100" style={{ backgroundColor: "rgba(var(--hc-white-rgb), 0.10)" }}>
                                    <div className="small text-white-50 mb-1">Client</div>
                                    <div className="fw-semibold">{provider?.account_name || "-"}</div>
                                </div>
                            </div>
                        </div>

                        <div className="row g-3">
                            <div className="col-md-6">
                                <div className="p-3 rounded-4 border border-white/20 text-white h-100" style={{ backgroundColor: "rgba(var(--hc-white-rgb), 0.10)" }}>
                                    <div className="small text-white-50 mb-1">NPI number</div>
                                    <div className="fw-semibold">{provider?.npi_number || "-"}</div>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="p-3 rounded-4 border border-white/20 text-white h-100" style={{ backgroundColor: "rgba(var(--hc-white-rgb), 0.10)" }}>
                                    <div className="small text-white-50 mb-1">License ID</div>
                                    <div className="fw-semibold">{provider?.license_id || "-"}</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="col-lg-12 col-xl-4 d-flex">
                        <div className="p-4 rounded-4 border border-white/20 text-white w-100 h-100 shadow-sm" style={{ backgroundColor: "rgba(var(--hc-white-rgb), 0.10)", backdropFilter: "blur(16px)", WebkitBackdropFilter: "blur(16px)" }}>
                            <div className="d-flex align-items-center gap-2 mb-3">
                                <MdPerson size={18} />
                                <span className="fw-semibold">Contact snapshot</span>
                            </div>
                            <div className="d-flex flex-column gap-3">
                                <div>
                                    <div className="small text-white-50">Email</div>
                                    <div className="fw-semibold">{provider?.work_email || provider?.personal_email || "-"}</div>
                                </div>
                                <div>
                                    <div className="small text-white-50">Phone</div>
                                    <div className="fw-semibold">{provider?.mobile_phone || provider?.home_phone || "-"}</div>
                                </div>
                                <div>
                                    <div className="small text-white-50">Address</div>
                                    <div className="fw-semibold">{formatAddress(primaryAddress)}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row g-4 mb-4">
                <div className="col-lg-6 col-xl-3 col-md-6">
                    <StatCard
                        label="Client"
                        value={provider?.account_name || "-"}
                        icon={MdBusiness}
                        iconBg="var(--hc-icon-mint-bg)"
                        valueClass="text-success"
                    />
                </div>
                <div className="col-lg-6 col-xl-3 col-md-6">
                    <StatCard
                        label="Created on"
                        value={formatDate(provider?.created_on)}
                        icon={MdCalendarMonth}
                        iconBg="var(--hc-icon-sky-bg)"
                        valueClass="text-primary"
                    />
                </div>
                <div className="col-lg-6 col-xl-3 col-md-6">
                    <StatCard
                        label="Modified on"
                        value={formatDate(provider?.modified_on)}
                        icon={MdFactCheck}
                        iconBg="var(--hc-status-pending-bg)"
                        valueClass="text-warning"
                    />
                </div>
                <div className="col-lg-6 col-xl-3 col-md-6">
                    <StatCard
                        label="Authorized to work"
                        value={formatBoolean(provider?.is_authorized_to_work)}
                        icon={MdBadge}
                        iconBg="var(--hc-icon-violet-bg)"
                        valueClass="text-purple"
                    />
                </div>
            </div>

            <div className="row g-4 mb-4">
                <div className="col-lg-6">
                    <SectionCard title="Professional overview" icon={MdPerson}>
                        <DetailRow icon={MdPerson} label="Full name" value={fullName} />
                        <DetailRow icon={MdBusiness} label="Title" value={provider?.provider_title_name || "-"} />
                        <DetailRow icon={MdMedicalServices} label="Telemedicine experience" value={provider?.telemed_exp_name || "-"} />
                        <DetailRow icon={MdDescription} label="NPI number" value={provider?.npi_number || "-"} />
                        <DetailRow icon={MdGroups} label="License ID" value={provider?.license_id || "-"} />
                        <DetailRow icon={MdCalendarMonth} label="DOB" value={formatDate(provider?.date_of_birth)} />
                    </SectionCard>
                </div>
                <div className="col-lg-6">
                    <SectionCard title="Location & Credentials" icon={MdLocationOn}>
                        {/* <DetailRow icon={MdLocationOn} label="Home Address" value={formatAddress(primaryAddress)} /> */}
                        <DetailRow icon={MdLocationOn} label="Birth address" value={formatAddress(birthAddress)} />
                        <DetailRow icon={MdLocationOn} label="Primary Location address" value={formatAddress(primaryLocationAddress)} />
                        <DetailRow icon={MdLocationOn} label="Secondary Location address" value={formatAddress(secondaryLocationAddress)} />
                        {/* <DetailRow icon={MdEmail} label="Personal email" value={provider?.personal_email || "-"} />
                        <DetailRow icon={MdPhone} label="Mobile phone" value={provider?.mobile_phone || "-"} /> */}
                        <DetailRow icon={MdDescription} label="License state" value={provider?.license_state || "-"} />
                        <DetailRow icon={MdCalendarMonth} label="License issue date" value={formatDate(provider?.license_issue_date)} />
                    </SectionCard>
                </div>
            </div>

            <div className="row g-4 mb-4">
                <div className="col-12">
                    <SectionCard title="Education & Training" icon={MdSchool}>
                        {sectionLoading.education ? (
                            <div className="row g-3">
                                {[1, 2].map((item) => (
                                    <div className="col-12" key={item}>
                                        <div className="row g-3">
                                            {[1, 2, 3, 4].map((card) => (
                                                <div className="col-12 col-md-6" key={card}>
                                                    <div className="h-100 rounded-4 border border-light-subtle bg-light p-3">
                                                        <div className="skeleton mb-2 rounded-2" style={{ height: 12, width: "45%" }} />
                                                        <div className="skeleton position-relative overflow-hidden rounded-2 w-100" style={{ height: 16 }} />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : educations.length ? (
                            <div className="row g-3">
                                {pagedEducations.map((item) => (
                                    <div className="col-12" key={item.id}>
                                        <div className="row g-3">
                                            <DetailInfoCard label="Education" value={item.education_type_name} />
                                            <DetailInfoCard label="Institution" value={item.institution_name} />
                                            <DetailInfoCard label="Speciality" value={item.specialty} />
                                            <DetailInfoCard label="Degree" value={item.degree_earned_name} />
                                            <DetailInfoCard label="Program Completed" value={item.program_completed_name} />
                                            <DetailInfoCard label="Start Date" value={formatDate(item.start_date)} />
                                            <DetailInfoCard label="End Date" value={formatDate(item.end_date)} />
                                            <DetailInfoCard label="Program Director Name" value={item.program_director_name} />
                                            <DetailInfoCard label="Program Director Email" value={item.program_director_email} />
                                            <DetailInfoCard label="Program Director Phone" value={item.program_director_phone} />
                                            <DetailInfoCard label="Program Director Fax" value={item.program_director_fax} />
                                            {item.file_id && item.file_name ? (
                                                <DetailInfoCard
                                                    label={`File - ${item.file_name}`}
                                                    actions={renderFileActionButtons(item)}
                                                />
                                            ) : null}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-muted text-center">No education records found.</div>
                        )}
                        {!sectionLoading.education && educations.length > pageSize ? (
                            <div className="d-flex justify-content-end gap-2 mt-2">
                                <button
                                    type="button"
                                    className="btn btn-sm hc-btn-prev px-5 rounded-3 py-2 shadow"
                                    onClick={() => setEducationPage((prev) => Math.max(prev - 1, 0))}
                                    disabled={educationPage === 0}
                                >
                                    Prev\
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-sm hc-btn-next border-0 px-5 py-2 rounded-3 shadow"
                                    onClick={() => setEducationPage((prev) => prev + 1)}
                                    disabled={(educationPage + 1) * pageSize >= educations.length}
                                >
                                    Next
                                </button>
                            </div>
                        ) : null}
                    </SectionCard>
                </div>
                <div className="col-12">
                    <SectionCard title="Exams" icon={MdAssignmentTurnedIn}>
                        {sectionLoading.exams ? (
                            <div className="row g-3">
                                {[1, 2].map((item) => (
                                    <div className="col-12" key={item}>
                                        <div className="row g-3">
                                            {[1, 2, 3, 4].map((card) => (
                                                <div className="col-12 col-md-6" key={card}>
                                                    <div className="h-100 rounded-4 border border-light-subtle bg-light p-3">
                                                        <div className="skeleton mb-2 rounded-2" style={{ height: 12, width: "45%" }} />
                                                        <div className="skeleton position-relative overflow-hidden rounded-2 w-100" style={{ height: 16 }} />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : exams.length ? (
                            <div className="row g-3">
                                {pagedExams.map((item) => (
                                    <div className="col-12" key={item.id || item.exam_type_name}>
                                        <div className="row g-3">
                                            <DetailInfoCard label="Exam Name" value={item.exam_type_name} />
                                            <DetailInfoCard label="Exam Date" value={formatDate(item.exam_date)} />
                                            <DetailInfoCard label="First Try Date" value={formatDate(item.first_try_date)} />
                                            <DetailInfoCard label="Exam Location" value={item.location} />
                                            <DetailInfoCard label="Exam Passed" value={formatBoolean(item.exam_passed)} />
                                            <DetailInfoCard label="Exam Score" value={item.score} />
                                            <DetailInfoCard label="Status" value={item.status_name} />
                                            <DetailInfoCard label="No of Attempts" value={item.no_of_attempts} />
                                            {item.file_id && item.file_name ? (
                                                <DetailInfoCard
                                                    label={`File - ${item.file_name}`}
                                                    actions={renderFileActionButtons(item)}
                                                />
                                            ) : null}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-muted text-center">No exam records found.</div>
                        )}
                        {!sectionLoading.exams && exams.length > pageSize ? (
                            <div className="d-flex justify-content-end gap-2 mt-2">
                                <button
                                    type="button"
                                    className="btn btn-sm hc-btn-prev px-5 rounded-3 py-2 shadow"
                                    onClick={() => setExamPage((prev) => Math.max(prev - 1, 0))}
                                    disabled={examPage === 0}
                                >
                                    Prev
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-sm hc-btn-next border-0 px-5 py-2 rounded-3 shadow"
                                    onClick={() => setExamPage((prev) => prev + 1)}
                                    disabled={(examPage + 1) * pageSize >= exams.length}
                                >
                                    Next
                                </button>
                            </div>
                        ) : null}
                    </SectionCard>
                </div>
            </div>

            <div className="row g-4 mb-4">
                <div className="col-12">
                    <SectionCard title="Practice / Employer" icon={MdWork}>
                        {sectionLoading.employers ? (
                            <div className="row g-3">
                                {[1, 2].map((item) => (
                                    <div className="col-12" key={item}>
                                        <div className="row g-3">
                                            {[1, 2, 3, 4].map((card) => (
                                                <div className="col-12 col-md-6" key={card}>
                                                    <div className="h-100 rounded-4 border border-light-subtle bg-light p-3">
                                                        <div className="skeleton mb-2 rounded-2" style={{ height: 12, width: "45%" }} />
                                                        <div className="skeleton position-relative overflow-hidden rounded-2 w-100" style={{ height: 16 }} />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : employers.length ? (
                            <div className="row g-3">
                                {pagedEmployers.map((item) => (
                                    <div className="col-12" key={item.id || item.employer_name}>
                                        <div className="row g-3">
                                            <DetailInfoCard label="Employer" value={item.employer_name} />
                                            <DetailInfoCard label="Office" value={item.office_type_name} />
                                            <DetailInfoCard label="Position" value={item.current_position_held} />
                                            <DetailInfoCard label="Employer Type" value={item.employer_type_name} />
                                            <DetailInfoCard label="Start Date" value={formatDate(item.start_date)} />
                                            <DetailInfoCard label="End Date" value={formatDate(item.end_date)} />
                                            <DetailInfoCard label="Staff Category" value={item.staff_category_name} />
                                            <DetailInfoCard label="Case Load" value={item.case_load} />
                                            <DetailInfoCard label="Accept New Patients" value={formatBoolean(item.accept_new_patient)} />
                                            <DetailInfoCard label="Patient Age Range" value={item.patient_age_range_name} />
                                            <DetailInfoCard label="Patient Type" value={item.patient_type_name} />
                                            <DetailInfoCard label="Patient gender" value={item.patient_gender_name} />
                                            <DetailInfoCard label="W9 Practice Name" value={item.w9_practice_name} />
                                            <DetailInfoCard label="Tax" value={item.tax_id} />
                                            <DetailInfoCard label="NPI" value={item.npi} />
                                            <DetailInfoCard label="Group Medicaid" value={item.group_medicaid} />
                                            <DetailInfoCard label="Group Medicare" value={item.group_medicare} />
                                            {item.file_id && item.file_name ? (
                                                <DetailInfoCard
                                                    label={`File - ${item.file_name}`}
                                                    actions={renderFileActionButtons(item)}
                                                />
                                            ) : null}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-muted text-center">No practice/employer records found.</div>
                        )}
                        {!sectionLoading.employers && employers.length > pageSize ? (
                            <div className="d-flex justify-content-end gap-2 mt-2">
                                <button
                                    type="button"
                                    className="btn btn-sm hc-btn-prev px-5 rounded-3 py-2 shadow"
                                    onClick={() => setEmployerPage((prev) => Math.max(prev - 1, 0))}
                                    disabled={employerPage === 0}
                                >
                                    Prev
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-sm hc-btn-next border-0 px-5 py-2 rounded-3 shadow"
                                    onClick={() => setEmployerPage((prev) => prev + 1)}
                                    disabled={(employerPage + 1) * pageSize >= employers.length}
                                >
                                    Next
                                </button>
                            </div>
                        ) : null}
                    </SectionCard>
                </div>
            </div>

            <div className="row g-4 mb-4">
                <div className="col-12">
                    <SectionCard title="Facility Affiliations" icon={MdBusiness}>
                        {sectionLoading.facilities ? (
                            <div className="row g-3">
                                {[1, 2].map((item) => (
                                    <div className="col-12" key={item}>
                                        <div className="row g-3">
                                            {[1, 2, 3, 4].map((card) => (
                                                <div className="col-12 col-md-6" key={card}>
                                                    <div className="h-100 rounded-4 border border-light-subtle bg-light p-3">
                                                        <div className="skeleton mb-2 rounded-2" style={{ height: 12, width: "45%" }} />
                                                        <div className="skeleton position-relative overflow-hidden rounded-2 w-100" style={{ height: 16 }} />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : facilities.length ? (
                            <div className="row g-3">
                                {pagedFacilities.map((item) => (
                                    <div className="col-12" key={item.id || item.facility_name}>
                                        <div className="row g-3">
                                            <DetailInfoCard label="Facility" value={item.facility_name} />
                                            <DetailInfoCard label="Primary Facility" value={formatBoolean(item.is_primary_affiliation)} />
                                            <DetailInfoCard label="Currently Affiliated" value={formatBoolean(item.currently_affiliated)} />
                                            <DetailInfoCard label="Start Date" value={formatDate(item.start_date)} />
                                            <DetailInfoCard label="End Date" value={formatDate(item.end_date)} />
                                            <DetailInfoCard label="Reason For Leaving" value={item.reason_for_leaving} />
                                            <DetailInfoCard label="Accept New Patients" value={formatBoolean(item.accept_new_patients)} />
                                            <DetailInfoCard label="Telemedicine" value={formatBoolean(item.telemedicine)} />
                                            <DetailInfoCard label="Take Calls" value={formatBoolean(item.take_calls)} />
                                            <DetailInfoCard label="In Good Standing" value={formatBoolean(item.in_good_standing)} />
                                            <DetailInfoCard label="Staff Category" value={item.staff_category_name} />
                                            <DetailInfoCard label="Admitting Privileges" value={formatBoolean(item.admitting_privileges)} />
                                            <DetailInfoCard label="Covering Physicians" value={item.covering_physicians} />
                                            <DetailInfoCard label="Approx Annual Patients" value={item.approx_annual_patient_encounter} />
                                            <DetailInfoCard label="Initial Appointment Date" value={formatDate(item.initial_appointment_date)} />
                                            <DetailInfoCard label="Last Appointment Date" value={formatDate(item.last_appointment_date)} />
                                            <DetailInfoCard label="Appointment End Date" value={formatDate(item.appointment_end_date)} />
                                            <DetailInfoCard label="Reappointment Start Date" value={formatDate(item.reappointment_start_date)} />
                                            {item.file_id && item.file_name ? (
                                                <DetailInfoCard
                                                    label={`File - ${item.file_name}`}
                                                    actions={renderFileActionButtons(item)}
                                                />
                                            ) : null}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-muted text-center">No facility affiliations found.</div>
                        )}
                        {!sectionLoading.facilities && facilities.length > pageSize ? (
                            <div className="d-flex justify-content-end gap-2 mt-2">
                                <button
                                    type="button"
                                    className="btn btn-sm hc-btn-prev px-5 rounded-3 py-2 shadow"
                                    onClick={() => setFacilityPage((prev) => Math.max(prev - 1, 0))}
                                    disabled={facilityPage === 0}
                                >
                                    Prev
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-sm hc-btn-next border-0 px-5 py-2 rounded-3 shadow"
                                    onClick={() => setFacilityPage((prev) => prev + 1)}
                                    disabled={(facilityPage + 1) * pageSize >= facilities.length}
                                >
                                    Next
                                </button>
                            </div>
                        ) : null}
                    </SectionCard>
                </div>
            </div>

            <div className="row g-4 mb-4">
                <div className="col-12">
                    <SectionCard title="Work History" icon={MdWork}>
                        {sectionLoading.workHistory ? (
                            <div className="row g-3">
                                {[1, 2].map((item) => (
                                    <div className="col-12" key={item}>
                                        <div className="row g-3">
                                            {[1, 2, 3, 4].map((card) => (
                                                <div className="col-12 col-md-6" key={card}>
                                                    <div className="h-100 rounded-4 border border-light-subtle bg-light p-3">
                                                        <div className="skeleton mb-2 rounded-2" style={{ height: 12, width: "45%" }} />
                                                        <div className="skeleton position-relative overflow-hidden rounded-2 w-100" style={{ height: 16 }} />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : workHistory.length ? (
                            <div className="row g-3">
                                {pagedWorkHistory.map((item) => (
                                    <div className="col-12" key={item.id || item.employer_name}>
                                        <div className="row g-3">
                                            <DetailInfoCard label="Employer" value={item.employer_name} />
                                            <DetailInfoCard label="Currently Employed" value={formatBoolean(item.is_currently_employed)} />
                                            <DetailInfoCard label="Practice Type" value={item.practice_type_name} />
                                            <DetailInfoCard label="Position Held" value={item.position_held} />
                                            <DetailInfoCard label="Start Date" value={formatDate(item.start_date)} />
                                            <DetailInfoCard label="End Date" value={formatDate(item.end_date)} />
                                            <DetailInfoCard label="Employer In" value={item.employer_in_name} />
                                            <DetailInfoCard label="Reason For Leaving" value={item.reason_for_leaving} />
                                            {item.file_id && item.file_name ? (
                                                <DetailInfoCard
                                                    label={`File - ${item.file_name}`}
                                                    actions={renderFileActionButtons(item)}
                                                />
                                            ) : null}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-muted text-center">No work history records found.</div>
                        )}
                        {!sectionLoading.workHistory && workHistory.length > pageSize ? (
                            <div className="d-flex justify-content-end gap-2 mt-2">
                                <button
                                    type="button"
                                    className="btn btn-sm hc-btn-prev px-5 rounded-3 py-2 shadow"
                                    onClick={() => setWorkHistoryPage((prev) => Math.max(prev - 1, 0))}
                                    disabled={workHistoryPage === 0}
                                >
                                    Prev
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-sm hc-btn-next border-0 px-5 py-2 rounded-3 shadow"
                                    onClick={() => setWorkHistoryPage((prev) => prev + 1)}
                                    disabled={(workHistoryPage + 1) * pageSize >= workHistory.length}
                                >
                                    Next
                                </button>
                            </div>
                        ) : null}
                    </SectionCard>
                </div>
            </div>

            <div className="row g-4 mb-4">
                <div className="col-12">
                    <SectionCard title="Military experience" icon={MdMedicalServices}>
                        {sectionLoading.military ? (
                            <div className="row g-3">
                                {[1, 2].map((item) => (
                                    <div className="col-12" key={item}>
                                        <div className="row g-3">
                                            {[1, 2, 3, 4].map((card) => (
                                                <div className="col-12 col-md-6" key={card}>
                                                    <div className="h-100 rounded-4 border border-light-subtle bg-light p-3">
                                                        <div className="skeleton mb-2 rounded-2" style={{ height: 12, width: "45%" }} />
                                                        <div className="skeleton position-relative overflow-hidden rounded-2 w-100" style={{ height: 16 }} />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : military.length ? (
                            <div className="row g-3">
                                {pagedMilitary.map((item) => (
                                    <div className="col-12" key={item.id || item.branch_name}>
                                        <div className="row g-3">
                                            <DetailInfoCard label="Branch" value={item.branch_name} />
                                            <DetailInfoCard label="Rank" value={item.rank_name} />
                                            <DetailInfoCard label="Role" value={item.role_name} />
                                            <DetailInfoCard label="Service" value={item.service} />
                                            <DetailInfoCard label="Currently Serving" value={formatBoolean(item.is_currently_serving)} />
                                            <DetailInfoCard label="Start Date" value={formatDate(item.start_date)} />
                                            <DetailInfoCard label="End Date" value={formatDate(item.end_date)} />
                                            <DetailInfoCard label="Discharge Type" value={item.discharge_type_name} />
                                            <DetailInfoCard label="Last Served Location" value={item.last_military_service_location} />
                                            {item.file_id && item.file_name ? (
                                                <DetailInfoCard
                                                    label={`File - ${item.file_name}`}
                                                    actions={renderFileActionButtons(item)}
                                                />
                                            ) : null}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-muted text-center">No military experience found.</div>
                        )}{!sectionLoading.military && military.length > pageSize ? (
                            <div className="d-flex justify-content-end gap-2 mt-2">
                                <button
                                    type="button"
                                    className="btn btn-sm hc-btn-prev px-5 rounded-3 py-2 shadow"
                                    onClick={() => setMilitaryPage((prev) => Math.max(prev - 1, 0))}
                                    disabled={militaryPage === 0}
                                >
                                    Prev
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-sm hc-btn-next border-0 px-5 py-2 rounded-3 shadow"
                                    onClick={() => setMilitaryPage((prev) => prev + 1)}
                                    disabled={(militaryPage + 1) * pageSize >= military.length}
                                >
                                    Next
                                </button>
                            </div>
                        ) : null}
                    </SectionCard>
                </div>
            </div>

            <div className="row g-4 mb-4">
                <div className="col-12">
                    <SectionCard title="Peer references" icon={MdGroups}>
                        {sectionLoading.peerReferences ? (
                            <div className="row g-3">
                                {[1, 2].map((item) => (
                                    <div className="col-12" key={item}>
                                        <div className="row g-3">
                                            {[1, 2, 3, 4].map((card) => (
                                                <div className="col-12 col-md-6" key={card}>
                                                    <div className="h-100 rounded-4 border border-light-subtle bg-light p-3">
                                                        <div className="skeleton mb-2 rounded-2" style={{ height: 12, width: "45%" }} />
                                                        <div className="skeleton position-relative overflow-hidden rounded-2 w-100" style={{ height: 16 }} />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : peerReferences.length ? (
                            <div className="row g-3">
                                {pagedPeerReferences.map((item) => (
                                    <div className="col-12" key={item.id || item.reference_name}>
                                        <div className="row g-3">
                                            <DetailInfoCard label="Reference Name" value={item.reference_name} />
                                            <DetailInfoCard label="Degree" value={item.degree_name} />
                                            <DetailInfoCard label="Speciality" value={item.specialty} />
                                            <DetailInfoCard label="Reference Title" value={item.reference_title} />
                                            <DetailInfoCard label="RelationShip Type" value={item.relationship_type} />
                                            <DetailInfoCard label="Length Time" value={item.length_time} />
                                            <DetailInfoCard label="Reference Date" value={formatDate(item.reference_date)} />
                                            <DetailInfoCard label="Email" value={item.email} />
                                            <DetailInfoCard label="Phone" value={item.phone} />
                                            <DetailInfoCard label="Fax" value={item.fax} />
                                            <DetailInfoCard label="Facility Name" value={item.facility_name} />
                                            {item.file_id && item.file_name ? (
                                                <DetailInfoCard
                                                    label={`File - ${item.file_name}`}
                                                    actions={renderFileActionButtons(item)}
                                                />
                                            ) : null}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-muted text-center">No peer references found.</div>
                        )}
                        {!sectionLoading.peerReferences && peerReferences.length > pageSize ? (
                            <div className="d-flex justify-content-end gap-2 mt-2">
                                <button
                                    type="button"
                                    className="btn btn-sm hc-btn-prev px-5 rounded-3 py-2 shadow"
                                    onClick={() => setPeerReferencePage((prev) => Math.max(prev - 1, 0))}
                                    disabled={peerReferencePage === 0}
                                >
                                    Prev
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-sm hc-btn-next border-0 px-5 py-2 rounded-3 shadow"
                                    onClick={() => setPeerReferencePage((prev) => prev + 1)}
                                    disabled={(peerReferencePage + 1) * pageSize >= peerReferences.length}
                                >
                                    Next
                                </button>
                            </div>
                        ) : null}
                    </SectionCard>
                </div>
            </div>

            <div className="row g-4 mb-4">
                <div className="col-12">
                    <SectionCard title="State License" icon={MdDescription}>
                        {sectionLoading.stateLicenses ? (
                            <div className="row g-3">
                                {[1, 2].map((item) => (
                                    <div className="col-12" key={item}>
                                        <div className="row g-3">
                                            {[1, 2, 3, 4].map((card) => (
                                                <div className="col-12 col-md-6" key={card}>
                                                    <div className="h-100 rounded-4 border border-light-subtle bg-light p-3">
                                                        <div className="skeleton mb-2 rounded-2" style={{ height: 12, width: "45%" }} />
                                                        <div className="skeleton position-relative overflow-hidden rounded-2 w-100" style={{ height: 16 }} />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : stateLicenses.length ? (
                            <div className="row g-3">
                                {pagedStateLicenses.map((item, index) => (
                                    <div className="col-12" key={item.id || `${item.license_type_name}-${index}`}>
                                        <div className="row g-3">
                                            <DetailInfoCard label="License" value={item.license} />
                                            <DetailInfoCard label="License State" value={item.license_state} />
                                            <DetailInfoCard label="Multi State" value={formatBoolean(item.is_multi_state)} />
                                            <DetailInfoCard label="Primary License" value={formatBoolean(item.is_primary_license)} />
                                            <DetailInfoCard label="Issue Date" value={formatDate(item.issue_date || item.license_issue_date)} />
                                            <DetailInfoCard label="Expiration Date" value={formatDate(item.expiration_date)} />
                                            <DetailInfoCard label="License Type" value={item.provider_license_type_name || item.license_type_name} />
                                            <DetailInfoCard label="Taxonomy" value={item.taxonomy_code_name} />
                                            <DetailInfoCard label="Fee Exemption" value={item.fee_exemption} />
                                            <DetailInfoCard label="Enrolled Prescription" value={formatBoolean(item.enrolled_prescription_drug_program)} />
                                            <DetailInfoCard label="Additional Info" value={formatBoolean(item.additional_info)} />
                                            <DetailInfoCard label="Board Action" value={item.board_action} />
                                            {item.file_id && item.file_name ? (
                                                <DetailInfoCard
                                                    label={`File - ${item.file_name}`}
                                                    actions={renderFileActionButtons(item)}
                                                />
                                            ) : null}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-muted text-center">No state licenses found.</div>
                        )}
                        {!sectionLoading.stateLicenses && stateLicenses.length > pageSize ? (
                            <div className="d-flex justify-content-end gap-2 mt-2">
                                <button
                                    type="button"
                                    className="btn btn-sm hc-btn-prev px-5 rounded-3 py-2 shadow"
                                    onClick={() => setStateLicensePage((prev) => Math.max(prev - 1, 0))}
                                    disabled={stateLicensePage === 0}
                                >
                                    Prev
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-sm hc-btn-next border-0 px-5 py-2 rounded-3 shadow"
                                    onClick={() => setStateLicensePage((prev) => prev + 1)}
                                    disabled={(stateLicensePage + 1) * pageSize >= stateLicenses.length}
                                >
                                    Next
                                </button>
                            </div>
                        ) : null}
                    </SectionCard>
                </div>
            </div>

            <div className="row g-4 mb-4">
                <div className="col-12">
                    <SectionCard title="DEA License" icon={MdDescription}>
                        {sectionLoading.deaLicenses ? (
                            <div className="row g-3">
                                {[1, 2].map((item) => (
                                    <div className="col-12" key={item}>
                                        <div className="row g-3">
                                            {[1, 2, 3, 4].map((card) => (
                                                <div className="col-12 col-md-6" key={card}>
                                                    <div className="h-100 rounded-4 border border-light-subtle bg-light p-3">
                                                        <div className="skeleton mb-2 rounded-2" style={{ height: 12, width: "45%" }} />
                                                        <div className="skeleton position-relative overflow-hidden rounded-2 w-100" style={{ height: 16 }} />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : deaLicenses.length ? (
                            <div className="row g-3">
                                {pagedDEALicenses.map((item, index) => (
                                    <div className="col-12" key={item.id || `${item.license_type_name}-${index}`}>
                                        <div className="row g-3">
                                            <DetailInfoCard label="License" value={item.license} />
                                            <DetailInfoCard label="License State" value={item.license_state} />
                                            <DetailInfoCard label="Multi State" value={formatBoolean(item.is_multi_state)} />
                                            <DetailInfoCard label="Primary License" value={formatBoolean(item.is_primary_license)} />
                                            <DetailInfoCard label="Issue Date" value={formatDate(item.issue_date)} />
                                            <DetailInfoCard label="Expiration Date" value={formatDate(item.expiration_date)} />
                                            <DetailInfoCard label="License Type" value={item.provider_license_type_name || item.license_type_name} />
                                            <DetailInfoCard label="Taxonomy" value={item.taxonomy_code_name} />
                                            <DetailInfoCard label="Fee Exemption" value={item.fee_exemption} />
                                            <DetailInfoCard label="Enrolled Prescription" value={formatBoolean(item.enrolled_prescription_drug_program)} />
                                            <DetailInfoCard label="Additional Info" value={formatBoolean(item.additional_info)} />
                                            <DetailInfoCard label="Board Action" value={item.board_action} />
                                            {item.file_id && item.file_name ? (
                                                <DetailInfoCard
                                                    label={`File - ${item.file_name}`}
                                                    actions={renderFileActionButtons(item)}
                                                />
                                            ) : null}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-muted text-center">No DEA licenses found.</div>
                        )}
                        {!sectionLoading.deaLicenses && deaLicenses.length > pageSize ? (
                            <div className="d-flex justify-content-end gap-2 mt-2">
                                <button
                                    type="button"
                                    className="btn btn-sm hc-btn-prev px-5 rounded-3 py-2 shadow"
                                    onClick={() => setDeaLicensePage((prev) => Math.max(prev - 1, 0))}
                                    disabled={deaLicensePage === 0}
                                >
                                    Prev
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-sm hc-btn-next border-0 px-5 py-2 rounded-3 shadow"
                                    onClick={() => setDeaLicensePage((prev) => prev + 1)}
                                    disabled={(deaLicensePage + 1) * pageSize >= deaLicenses.length}
                                >
                                    Next
                                </button>
                            </div>
                        ) : null}
                    </SectionCard>
                </div>
            </div>

            <div className="row g-4 mb-4">
                <div className="col-12">
                    <SectionCard title="State Controlled Substance License" icon={MdDescription}>
                        {sectionLoading.controlledLicenses ? (
                            <div className="row g-3">
                                {[1, 2].map((item) => (
                                    <div className="col-12" key={item}>
                                        <div className="row g-3">
                                            {[1, 2, 3, 4].map((card) => (
                                                <div className="col-12 col-md-6" key={card}>
                                                    <div className="h-100 rounded-4 border border-light-subtle bg-light p-3">
                                                        <div className="skeleton mb-2 rounded-2" style={{ height: 12, width: "45%" }} />
                                                        <div className="skeleton position-relative overflow-hidden rounded-2 w-100" style={{ height: 16 }} />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : controlledLicenses.length ? (
                            <div className="row g-3">
                                {pagedControlledLicenses.map((item, index) => (
                                    <div className="col-12" key={item.id || `${item.license_type_name}-${index}`}>
                                        <div className="row g-3">
                                            <DetailInfoCard label="License" value={item.license} />
                                            <DetailInfoCard label="License State" value={item.license_state} />
                                            <DetailInfoCard label="Multi State" value={formatBoolean(item.is_multi_state)} />
                                            <DetailInfoCard label="Primary License" value={formatBoolean(item.is_primary_license)} />
                                            <DetailInfoCard label="Issue Date" value={formatDate(item.issue_date)} />
                                            <DetailInfoCard label="Expiration Date" value={formatDate(item.expiration_date)} />
                                            <DetailInfoCard label="License Type" value={item.provider_license_type_name || item.license_type_name} />
                                            <DetailInfoCard label="Taxonomy" value={item.taxonomy_code_name} />
                                            <DetailInfoCard label="Fee Exemption" value={item.fee_exemption} />
                                            <DetailInfoCard label="Enrolled Prescription" value={formatBoolean(item.enrolled_prescription_drug_program)} />
                                            <DetailInfoCard label="Additional Info" value={formatBoolean(item.additional_info)} />
                                            <DetailInfoCard label="Board Action" value={item.board_action} />
                                            {item.file_id && item.file_name ? (
                                                <DetailInfoCard
                                                    label={`File - ${item.file_name}`}
                                                    actions={renderFileActionButtons(item)}
                                                />
                                            ) : null}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-muted text-center">No state controlled substance licenses found.</div>
                        )}
                        {!sectionLoading.controlledLicenses && controlledLicenses.length > pageSize ? (
                            <div className="d-flex justify-content-end gap-2 mt-2">
                                <button
                                    type="button"
                                    className="btn btn-sm hc-btn-prev px-5 rounded-3 py-2 shadow"
                                    onClick={() => setControlledLicensePage((prev) => Math.max(prev - 1, 0))}
                                    disabled={controlledLicensePage === 0}
                                >
                                    Prev
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-sm hc-btn-next border-0 px-5 py-2 rounded-3 shadow"
                                    onClick={() => setControlledLicensePage((prev) => prev + 1)}
                                    disabled={(controlledLicensePage + 1) * pageSize >= controlledLicenses.length}
                                >
                                    Next
                                </button>
                            </div>
                        ) : null}
                    </SectionCard>
                </div>
            </div>

            <div className="row g-4 mb-4">
                <div className="col-12">
                    <SectionCard title="Board Certificate" icon={MdBadge}>
                        {sectionLoading.boardCertificates ? (
                            <div className="row g-3">
                                {[1, 2].map((item) => (
                                    <div className="col-12" key={item}>
                                        <div className="row g-3">
                                            {[1, 2, 3, 4].map((card) => (
                                                <div className="col-12 col-md-6" key={card}>
                                                    <div className="h-100 rounded-4 border border-light-subtle bg-light p-3">
                                                        <div className="skeleton mb-2 rounded-2" style={{ height: 12, width: "45%" }} />
                                                        <div className="skeleton position-relative overflow-hidden rounded-2 w-100" style={{ height: 16 }} />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : boardCertificates.length ? (
                            <div className="row g-3">
                                {pagedBoardCertificates.map((item, index) => (
                                    <div className="col-12" key={item.id || `${item.board_name_name}-${index}`}>
                                        <div className="row g-3">
                                            <DetailInfoCard label="Certification" value={item.certifications} />
                                            <DetailInfoCard label="Board Name" value={item.board_name_name} />
                                            <DetailInfoCard label="Primary Board" value={formatBoolean(item.is_primary_board)} />
                                            <DetailInfoCard label="Board Eligible" value={formatBoolean(item.is_board_eligible)} />
                                            <DetailInfoCard label="Exam Passed" value={formatBoolean(item.exam_passed)} />
                                            <DetailInfoCard label="Speciality Exam" value={formatBoolean(item.speciality_exam)} />
                                            <DetailInfoCard label="Speciality Exam Passed" value={formatBoolean(item.speciality_exam_passed)} />
                                            <DetailInfoCard label="Speciality Exam Attempts" value={item.no_of_speciality_exam_attempts} />
                                            <DetailInfoCard label="Certification Status" value={item.cert_status} />
                                            <DetailInfoCard label="Certification Duration" value={item.certificate_duration} />
                                            <DetailInfoCard label="Issue Date" value={formatDate(item.issue_date)} />
                                            <DetailInfoCard label="Expiration Date" value={formatDate(item.expiration_date)} />
                                            <DetailInfoCard label="Recertification Date" value={formatDate(item.recertification_date)} />
                                            <DetailInfoCard label="MOC/OCC Status" value={item.moc_occ_status_name} />
                                            <DetailInfoCard label="Meet MOC/OCC" value={formatBoolean(item.meeting_moc_occ)} />
                                            {item.file_id && item.file_name ? (
                                                <DetailInfoCard
                                                    label={`File - ${item.file_name}`}
                                                    actions={renderFileActionButtons(item)}
                                                />
                                            ) : null}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-muted text-center">No board certificates found.</div>
                        )}
                        {!sectionLoading.boardCertificates && boardCertificates.length > pageSize ? (
                            <div className="d-flex justify-content-end gap-2 mt-2">
                                <button
                                    type="button"
                                    className="btn btn-sm hc-btn-prev px-5 rounded-3 py-2 shadow"
                                    onClick={() => setBoardCertificatePage((prev) => Math.max(prev - 1, 0))}
                                    disabled={boardCertificatePage === 0}
                                >
                                    Prev
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-sm hc-btn-next border-0 px-5 py-2 rounded-3 shadow"
                                    onClick={() => setBoardCertificatePage((prev) => prev + 1)}
                                    disabled={(boardCertificatePage + 1) * pageSize >= boardCertificates.length}
                                >
                                    Next
                                </button>
                            </div>
                        ) : null}
                    </SectionCard>
                </div>
            </div>

            <div className="row g-4 mb-4">
                <div className="col-12">
                    <SectionCard title="Professional Organizations" icon={MdBadge}>
                        {sectionLoading.professionalCertificates ? (
                            <div className="row g-3">
                                {[1, 2].map((item) => (
                                    <div className="col-12" key={item}>
                                        <div className="row g-3">
                                            {[1, 2, 3, 4].map((card) => (
                                                <div className="col-12 col-md-6" key={card}>
                                                    <div className="h-100 rounded-4 border border-light-subtle bg-light p-3">
                                                        <div className="skeleton mb-2 rounded-2" style={{ height: 12, width: "45%" }} />
                                                        <div className="skeleton position-relative overflow-hidden rounded-2 w-100" style={{ height: 16 }} />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : professionalCertificates.length ? (
                            <div className="row g-3">
                                {pagedProfessionalCertificates.map((item, index) => (
                                    <div className="col-12" key={item.id || `${item.board_name_name}-${index}`}>
                                        <div className="row g-3">
                                            <DetailInfoCard label="Certification" value={item.certifications} />
                                            <DetailInfoCard label="Board Name" value={item.board_name_name} />
                                            <DetailInfoCard label="Primary Board" value={formatBoolean(item.is_primary_board)} />
                                            <DetailInfoCard label="Board Eligible" value={formatBoolean(item.is_board_eligible)} />
                                            <DetailInfoCard label="Exam Passed" value={formatBoolean(item.exam_passed)} />
                                            <DetailInfoCard label="Speciality Exam" value={item.speciality_exam} />
                                            <DetailInfoCard label="Speciality Exam Passed" value={formatBoolean(item.speciality_exam_passed)} />
                                            <DetailInfoCard label="Speciality Exam Attempts" value={item.no_of_speciality_exam_attempts} />
                                            <DetailInfoCard label="Certification Status" value={item.cert_status} />
                                            <DetailInfoCard label="Certification Duration" value={item.certification_duration} />
                                            <DetailInfoCard label="Issue Date" value={formatDate(item.issue_date)} />
                                            <DetailInfoCard label="Expiration Date" value={formatDate(item.expiration_date)} />
                                            <DetailInfoCard label="Recertification Date" value={formatDate(item.recertification_date)} />
                                            <DetailInfoCard label="MOC/OCC Status" value={item.moc_occ_status_name} />
                                            <DetailInfoCard label="Meet MOC/OCC" value={formatBoolean(item.meeting_moc_occ)} />
                                            {item.file_id && item.file_name ? (
                                                <DetailInfoCard
                                                    label={`File - ${item.file_name}`}
                                                    actions={renderFileActionButtons(item)}
                                                />
                                            ) : null}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-muted text-center">No professional organization certificates found.</div>
                        )}
                        {!sectionLoading.professionalCertificates && professionalCertificates.length > pageSize ? (
                            <div className="d-flex justify-content-end gap-2 mt-2">
                                <button
                                    type="button"
                                    className="btn btn-sm hc-btn-prev px-5 rounded-3 py-2 shadow"
                                    onClick={() => setProfessionalCertificatePage((prev) => Math.max(prev - 1, 0))}
                                    disabled={professionalCertificatePage === 0}
                                >
                                    Prev
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-sm hc-btn-next border-0 px-5 py-2 rounded-3 shadow"
                                    onClick={() => setProfessionalCertificatePage((prev) => prev + 1)}
                                    disabled={(professionalCertificatePage + 1) * pageSize >= professionalCertificates.length}
                                >
                                    Next
                                </button>
                            </div>
                        ) : null}
                    </SectionCard>
                </div>
            </div>

            <div className="row g-4 mb-4">
                <div className="col-12">
                    <SectionCard title="Additional Certificate" icon={MdBadge}>
                        {sectionLoading.additionalCertificates ? (
                            <div className="row g-3">
                                {[1, 2].map((item) => (
                                    <div className="col-12" key={item}>
                                        <div className="row g-3">
                                            {[1, 2, 3, 4].map((card) => (
                                                <div className="col-12 col-md-6" key={card}>
                                                    <div className="h-100 rounded-4 border border-light-subtle bg-light p-3">
                                                        <div className="skeleton mb-2 rounded-2" style={{ height: 12, width: "45%" }} />
                                                        <div className="skeleton position-relative overflow-hidden rounded-2 w-100" style={{ height: 16 }} />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : additionalCertificates.length ? (
                            <div className="row g-3">
                                {pagedAdditionalCertificates.map((item, index) => (
                                    <div className="col-12" key={item.id || `${item.board_name_name}-${index}`}>
                                        <div className="row g-3">
                                            <DetailInfoCard label="Certification" value={item.certifications} />
                                            <DetailInfoCard label="Board Name" value={item.board_name_name} />
                                            <DetailInfoCard label="Primary Board" value={formatBoolean(item.is_primary_board)} />
                                            <DetailInfoCard label="Board Eligible" value={formatBoolean(item.is_board_eligible)} />
                                            <DetailInfoCard label="Exam Passed" value={formatBoolean(item.exam_passed)} />
                                            <DetailInfoCard label="Speciality Exam" value={item.speciality_exam} />
                                            <DetailInfoCard label="Speciality Exam Passed" value={formatBoolean(item.speciality_exam_passed)} />
                                            <DetailInfoCard label="Speciality Exam Attempts" value={item.no_of_speciality_exam_attempts} />
                                            <DetailInfoCard label="Certification Status" value={item.cert_status} />
                                            <DetailInfoCard label="Certification Duration" value={item.certification_duration} />
                                            <DetailInfoCard label="Issue Date" value={formatDate(item.issue_date)} />
                                            <DetailInfoCard label="Expiration Date" value={formatDate(item.expiration_date)} />
                                            <DetailInfoCard label="Recertification Date" value={formatDate(item.recertification_date)} />
                                            <DetailInfoCard label="MOC/OCC Status" value={item.moc_occ_status_name} />
                                            <DetailInfoCard label="Meet MOC/OCC" value={formatBoolean(item.meeting_moc_occ)} />
                                            {item.file_id && item.file_name ? (
                                                <DetailInfoCard
                                                    label={`File - ${item.file_name}`}
                                                    actions={renderFileActionButtons(item)}
                                                />
                                            ) : null}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-muted text-center">No additional certificates found.</div>
                        )}
                        {!sectionLoading.additionalCertificates && additionalCertificates.length > pageSize ? (
                            <div className="d-flex justify-content-end gap-2 mt-2">
                                <button
                                    type="button"
                                    className="btn btn-sm hc-btn-prev px-5 rounded-3 py-2 shadow"
                                    onClick={() => setAdditionalCertificatePage((prev) => Math.max(prev - 1, 0))}
                                    disabled={additionalCertificatePage === 0}
                                >
                                    Prev
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-sm hc-btn-next border-0 px-5 py-2 rounded-3 shadow"
                                    onClick={() => setAdditionalCertificatePage((prev) => prev + 1)}
                                    disabled={(additionalCertificatePage + 1) * pageSize >= additionalCertificates.length}
                                >
                                    Next
                                </button>
                            </div>
                        ) : null}
                    </SectionCard>
                </div>
            </div>

            <div className="row g-4 mb-4">
                <div className="col-12">
                    <SectionCard title="Medical Malpractice" icon={MdMedicalServices}>
                        {sectionLoading.malpractices ? (
                            <div className="row g-3">
                                {[1, 2].map((item) => (
                                    <div className="col-12" key={item}>
                                        <div className="row g-3">
                                            {[1, 2, 3, 4].map((card) => (
                                                <div className="col-12 col-md-6" key={card}>
                                                    <div className="h-100 rounded-4 border border-light-subtle bg-light p-3">
                                                        <div className="skeleton mb-2 rounded-2" style={{ height: 12, width: "45%" }} />
                                                        <div className="skeleton position-relative overflow-hidden rounded-2 w-100" style={{ height: 16 }} />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : malpractices.length ? (
                            <div className="row g-3">
                                {pagedMalpractices.map((item, index) => (
                                    <div className="col-12" key={item.id || `${item.insurer_name}-${index}`}>
                                        <div className="row g-3">
                                            <DetailInfoCard label="Insurer" value={item.insurer_name} />
                                            <DetailInfoCard label="Insurer State" value={item.insurer_state} />
                                            <DetailInfoCard label="Policy" value={item.policy} />
                                            <DetailInfoCard label="Primary Policy" value={formatBoolean(item.is_primary_policy)} />
                                            <DetailInfoCard label="Current Carrier" value={formatBoolean(item.current_carrier)} />
                                            <DetailInfoCard label="Effective Date" value={formatDate(item.policy_effective_date)} />
                                            <DetailInfoCard label="Expiration Date" value={formatDate(item.policy_expiration_date)} />
                                            <DetailInfoCard label="Retroactive Date" value={formatDate(item.retroactive_date)} />
                                            <DetailInfoCard label="Coverage Type" value={item.coverage_type_name} />
                                            <DetailInfoCard label="Carrier Years" value={item.carrier_years} />
                                            <DetailInfoCard label="Incident Amount" value={item.incident_amount} />
                                            <DetailInfoCard label="Aggregate Amount" value={item.aggregate_amount} />
                                            <DetailInfoCard label="Unlimited Coverage" value={formatBoolean(item.unlimited_coverage)} />
                                            <DetailInfoCard label="Organization Insured" value={item.organization_insured} />
                                            <DetailInfoCard label="Statutory Limit" value={formatBoolean(item.statutory_limit)} />
                                            <DetailInfoCard label="Contact Name" value={item.contact?.contact_name || item.contact_name} />
                                            <DetailInfoCard label="Contact Email" value={item.contact?.contact_email || item.contact_email} />
                                            <DetailInfoCard label="Contact Phone" value={item.contact?.contact_phone || item.contact_phone} />
                                            <DetailInfoCard label="Contact Fax" value={item.contact?.contact_fax || item.contact_fax} />
                                            {item.file_id && item.file_name ? (
                                                <DetailInfoCard
                                                    label={`File - ${item.file_name}`}
                                                    actions={renderFileActionButtons(item)}
                                                />
                                            ) : null}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-muted text-center">No malpractice records found.</div>
                        )}
                        {!sectionLoading.malpractices && malpractices.length > pageSize ? (
                            <div className="d-flex justify-content-end gap-2 mt-2">
                                <button
                                    type="button"
                                    className="btn btn-sm hc-btn-prev px-5 rounded-3 py-2 shadow"
                                    onClick={() => setMalpracticePage((prev) => Math.max(prev - 1, 0))}
                                    disabled={malpracticePage === 0}
                                >
                                    Prev
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-sm hc-btn-next border-0 px-5 py-2 rounded-3 shadow"
                                    onClick={() => setMalpracticePage((prev) => prev + 1)}
                                    disabled={(malpracticePage + 1) * pageSize >= malpractices.length}
                                >
                                    Next
                                </button>
                            </div>
                        ) : null}
                    </SectionCard>
                </div>
            </div>

            <div className="row g-4 mb-4">
                <div className="col-12">
                    <SectionCard title="Health Info" icon={MdFactCheck}>
                        {sectionLoading.healthInfos ? (
                            <div className="row g-3">
                                {[1, 2].map((item) => (
                                    <div className="col-12" key={item}>
                                        <div className="row g-3">
                                            {[1, 2, 3, 4].map((card) => (
                                                <div className="col-12 col-md-6" key={card}>
                                                    <div className="h-100 rounded-4 border border-light-subtle bg-light p-3">
                                                        <div className="skeleton mb-2 rounded-2" style={{ height: 12, width: "45%" }} />
                                                        <div className="skeleton position-relative overflow-hidden rounded-2 w-100" style={{ height: 16 }} />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : healthInfos.length ? (
                            <div className="row g-3">
                                {pagedHealthInfos.map((item, index) => (
                                    <div className="col-12" key={item.id || `${item.test_type_name}-${index}`}>
                                        <div className="row g-3">
                                            <DetailInfoCard label="Test Type" value={item.test_type_name} />
                                            <DetailInfoCard label="Status" value={item.status_name} />
                                            <DetailInfoCard label="Completion Date" value={formatDate(item.completion_date)} />
                                            <DetailInfoCard label="Expiration Date" value={formatDate(item.expiration_date)} />
                                            {item.file_id && item.file_name ? (
                                                <DetailInfoCard
                                                    label={`File - ${item.file_name}`}
                                                    actions={renderFileActionButtons(item)}
                                                />
                                            ) : null}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-muted text-center">No health info found.</div>
                        )}
                        {!sectionLoading.healthInfos && healthInfos.length > pageSize ? (
                            <div className="d-flex justify-content-end gap-2 mt-2">
                                <button
                                    type="button"
                                    className="btn btn-sm hc-btn-prev px-5 rounded-3 py-2 shadow"
                                    onClick={() => setHealthInfoPage((prev) => Math.max(prev - 1, 0))}
                                    disabled={healthInfoPage === 0}
                                >
                                    Prev
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-sm hc-btn-next border-0 px-5 py-2 rounded-3 shadow"
                                    onClick={() => setHealthInfoPage((prev) => prev + 1)}
                                    disabled={(healthInfoPage + 1) * pageSize >= healthInfos.length}
                                >
                                    Next
                                </button>
                            </div>
                        ) : null}
                    </SectionCard>
                </div>
            </div>
        </div>
    );
}
